import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack, styled } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { createCrmJob, CrmJobDTO } from 'wisestep-api';
import { ControlledTextInput, Label, WsButton, WsColors, WsLocationSearch } from 'wisestep-base';

import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import { WsTagsFilter } from '../../../components/filters/components';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { getValidationSchema } from '../helper/crmJobHelper';

const DrawerFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 1,
  justifyContent: 'revert',
  background: WsColors.TertiaryShades100,
  width: '50vw',
  padding: 2,
}));

interface CrmJobFormProps {
  defaultCrmJobData?: CrmJobDTO;
  handleClose: () => void;
  postSubmit?: (crmJob: CrmJobDTO) => void;
}

const CrmJobForm = ({ defaultCrmJobData, postSubmit, handleClose }: CrmJobFormProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const methods = useForm<CrmJobDTO>({
    resolver: yupResolver(getValidationSchema()),
    defaultValues: defaultCrmJobData || {
      title: '',
      linkedInJobUrl: '',
      jobDescription: '',
      jobFunction: '',
      clientId: '',
      client: null,
      location: null,
      jobStatus: 'NEW',
      jobClosed: false,
    },
  });

  const onSubmit = async (data: CrmJobDTO) => {
    try {
      setIsSaving(true);
      const resp = await createCrmJob(data);
      if (resp?.data) {
        setNotification(success('Job created successfully'));
        methods.reset();
        postSubmit?.(resp.data);
        handleClose();
      }
    } catch (error) {
      setNotification(failed('Error creating CRM job'));
      console.error('Error creating CRM job:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        <Box p={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ControlledTextInput
                name="title"
                type="text"
                fullWidth
                labelText="Job Title"
                variant="outlined"
                showMandatorySymbol={true}
                placeholder="Enter Job Title"
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextInput
                name="linkedInJobUrl"
                type="text"
                fullWidth
                labelText="Job URL"
                variant="outlined"
                showMandatorySymbol={true}
                placeholder="Enter URL"
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <WsLocationSearch
                size="small"
                placeHolder="Hiring Location"
                name="location"
                variant="outlined"
                multiSelect={false}
                showMandatorySymbol={true}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Client*</Label>
              <ClientAutocomplete name="client" placeHolder="Select Client" required={true} />
            </Grid>
            <Grid item p={1} sm={6}>
              <WsTagsFilter placeHolder="Enter Tags" name="tags" label="Tags" />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextInput
                name="jobDescription"
                type="text"
                fullWidth
                labelText="Description"
                variant="outlined"
                placeholder="Add Description"
                multiline
                rows={4}
                required={true}
              />
            </Grid>
          </Grid>
        </Box>
        <DrawerFooter>
          <Stack spacing={2} direction="row" justifyContent={'flex-end'}>
            <WsButton variant="outlined" size="large" onClick={handleClose}>
              Cancel
            </WsButton>
            <WsButton variant="contained" size="large" type="submit" disabled={isSaving}>
              Save
            </WsButton>
          </Stack>
        </DrawerFooter>
      </form>
    </FormProvider>
  );
};

export default CrmJobForm;
